import type { RarityTier } from '@gachakit/core/types/card';

const burstColors: Record<RarityTier, string> = {
  common: '#9eaa9b',
  uncommon: '#58d68d',
  rare: '#68c7ff',
  epic: '#e16f9d',
  legendary: '#f5c76b'
};

export function triggerRarityFeedback(rarity: RarityTier): void {
  triggerHaptics(rarity);

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  const burst = document.createElement('div');
  burst.className = 'rarity-burst';
  burst.setAttribute('data-rarity', rarity);
  burst.innerHTML = Array.from({ length: particleCount(rarity) }, (_, index) => {
    const angle = (360 / particleCount(rarity)) * index;
    const distance = 72 + (index % 4) * 18;
    return `<span style="--angle:${angle}deg; --distance:${distance}px"></span>`;
  }).join('');
  document.body.appendChild(burst);

  window.setTimeout(() => burst.remove(), 920);
}

function triggerHaptics(rarity: RarityTier): void {
  if (!('vibrate' in navigator)) {
    return;
  }

  const patterns: Record<RarityTier, number | number[]> = {
    common: 10,
    uncommon: [12, 22, 12],
    rare: [18, 34, 18],
    epic: [24, 34, 24, 52, 28],
    legendary: [36, 54, 30, 54, 44]
  };

  navigator.vibrate(patterns[rarity]);
}

function particleCount(rarity: RarityTier): number {
  switch (rarity) {
    case 'legendary':
      return 22;
    case 'epic':
      return 18;
    case 'rare':
      return 14;
    case 'uncommon':
      return 10;
    case 'common':
      return 7;
  }
}

const style = document.createElement('style');
style.textContent = `
  .rarity-burst {
    position: fixed;
    left: 50%;
    top: 45%;
    width: 1px;
    height: 1px;
    z-index: 60;
    pointer-events: none;
    --burst-color: #9eaa9b;
  }

  .rarity-burst[data-rarity='common'] { --burst-color: ${burstColors.common}; }
  .rarity-burst[data-rarity='uncommon'] { --burst-color: ${burstColors.uncommon}; }
  .rarity-burst[data-rarity='rare'] { --burst-color: ${burstColors.rare}; }
  .rarity-burst[data-rarity='epic'] { --burst-color: ${burstColors.epic}; }
  .rarity-burst[data-rarity='legendary'] { --burst-color: ${burstColors.legendary}; }

  .rarity-burst::before {
    content: "";
    position: absolute;
    width: 210px;
    height: 210px;
    border-radius: 50%;
    transform: translate(-50%, -50%) scale(0.2);
    border: 1px solid color-mix(in oklab, var(--burst-color) 62%, transparent);
    box-shadow: 0 0 42px color-mix(in oklab, var(--burst-color) 36%, transparent);
    animation: rarity-ring 760ms ease-out forwards;
  }

  .rarity-burst span {
    position: absolute;
    left: 0;
    top: 0;
    width: 6px;
    height: 18px;
    border-radius: 999px;
    background: linear-gradient(180deg, #ffffff, var(--burst-color));
    transform: rotate(var(--angle)) translateY(0) scale(0.7);
    transform-origin: center;
    opacity: 0;
    animation: rarity-particle 820ms cubic-bezier(0.18, 0.9, 0.22, 1) forwards;
  }

  @keyframes rarity-ring {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.2);
    }

    36% {
      opacity: 1;
    }

    100% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(1);
    }
  }

  @keyframes rarity-particle {
    0% {
      opacity: 0;
      transform: rotate(var(--angle)) translateY(0) scale(0.7);
    }

    18% {
      opacity: 1;
    }

    100% {
      opacity: 0;
      transform: rotate(var(--angle)) translateY(calc(var(--distance) * -1)) scale(0.2);
    }
  }
`;

if (!document.head.querySelector('style[data-rarity-feedback]')) {
  style.dataset.rarityFeedback = 'true';
  document.head.appendChild(style);
}
